<template>
  <div class="PostImagesUpload">
    <el-tooltip class="item" effect="dark" content="Vous avez atteint la limite d'envoi de photos"
                placement="bottom" v-if="isFieldDisabled">
      <el-button
          type="primary"
          size="mini"
          class="mb-4"
          @click.prevent.native="handleAddClick"
          :disabled="isFieldDisabled"
      >{{ $t("posts.components.picturesButton") }}
      </el-button>
    </el-tooltip>
    <el-button
        type="primary"
        size="mini"
        class="mb-4"
        @click.prevent.native="handleAddClick"
        :disabled="isFieldDisabled"
        v-else
    >{{ $t("posts.components.picturesButton") }}
    </el-button>

    <el-dialog class="PostImagesUpload-dialog" :visible="dialogVisible" @close="closeDialog()">
      <h3 slot="title">{{ $t("posts.components.picturesDialogTitle") }}</h3>
      <el-row
          :gutter="44"
          class="PostImagesUpload-container"
          :type="browserContext.up('md') ? 'flex' : null"
      >
        <el-col :lg="24" class="pb-3">
          <p>{{ $t("posts.components.picturesDialogTextTop") }} :</p>
          <el-upload
              accept="image/jpeg, image/jpg, image/png, image/webp, image/gif, image/heic, image/avif"
              action=""
              class="upload-demo"
              drag
              :on-change="handleFileSelect"
              :auto-upload="false"
              multiple
              :file-list="fileList"
              :limit="maximumPhotosAuthorizedToUpload"
              :on-exceed="limitUploadExceeded"
          >
            <em class="el-icon-upload"></em>
            <div class="el-upload__text" v-html="$t('posts.components.picturesDialogTextMiddle')"/>
            <div class="el-upload__tip" slot="tip" v-html="$t('posts.components.picturesDialogTextBottomEdito')"/>
          </el-upload>
        </el-col>
      </el-row>

      <div class="PostImagesUpload-actions">
        <el-button @click.prevent.native="dialogVisible = false" :disabled="convertingHeic">
          {{ $t("posts.components.picturesButtonCancel") }}
        </el-button>
        <el-button @click.prevent.native="addImages" type="primary" :loading="convertingHeic">
          {{ convertingHeic ? $t("posts.components.picturesButtonConverting") : $t("posts.components.picturesButtonAdd") }}
        </el-button>
      </div>
    </el-dialog>

    <!--        TODO: check https://sortablejs.github.io/Vue.Draggable/#/transition-example-2 to improve design-->
    <el-row :gutter="50" v-if="targetPhotos && targetPhotos.length > 0 && targetPhotos.some(photo => photo && photo.url)">
      <draggable :list="targetPhotos" group="people" @start="drag=true" @end="drag=false" v-bind="dragOptions" @change="onChange">
        <el-col :span="8" v-for="photo in targetPhotos" :key="photo.url" style="padding-bottom: 20px" v-if="photo && photo.url">
          <ubi-card :published="true" full-width class="uploaded-images-preview">
            <img :key="photo.url" :src="photo.url" height="215" alt="" class="uploaded-image"/>
            <template slot="footer">
              <el-button type="danger" size="mini" @click.prevent.native="handleRemove(photo)">
                {{ $t("posts.components.picturesButtonDelete") }}
              </el-button>
            </template>
          </ubi-card>
        </el-col>
      </draggable>
    </el-row>
  </div>
</template>

<style lang="scss">
@import "@/theme/_bootstrap.scss";

.PostImagesUpload {
  &-dialog .el-dialog {
    width: 670px;
    max-width: 90%;
  }

  &-container {
    > *:not(:first-child) {
      border-left: 1px solid $gray-200;
    }
  }

  &-actions {
    text-align: right;
  }

  .uploaded-images-preview {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 0;
    margin-left: 0;
  }

  .uploaded-image {
    margin: 0;
    padding: 0;
    max-width: 180px;
    object-fit: cover;
  }

}

.el-upload__tip {
  font-size: 12px;
  color: #606266;
  margin-top: 7px;
  line-height: normal;
}

.button {
  margin-top: 35px;
}
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.list-group {
  min-height: 20px;
}
.list-group-item {
  cursor: move;
}
.list-group-item i {
  cursor: pointer;
}
</style>

<script>
import { Dialog, Upload } from 'element-ui'
import draggable from 'vuedraggable'
import { isHeicFile, convertHeicToJpeg } from '@/utils/heicConverter'

export default {
  name: 'PostImagesUpload',
  inject: ['browserContext'],
  components: { ElDialog: Dialog, ElUpload: Upload, draggable },
  props: {
    post: {
      type: Object,
      required: true
    },
    network: {
      type: String,
      required: true,
      validator: function (value) {
        return ['initial', 'facebook', 'instagram', 'google', 'linkedin'].indexOf(value) !== -1
      }
    },
    desactiverChamp: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      file: '',
      fileList: [],
      dialogVisible: false,
      internalDisabled: false,
      convertingHeic: false
    }
  },
  watch: {
    // Watcher pour détecter les changements de photos et mettre à jour le statut
    targetPhotos: {
      handler (newPhotos) {
        // Ne plus appeler analyzePostMedias ici car cela peut écraser les valeurs
        // La mise à jour des statuts est gérée par updateMediaType() dans PostEditoCreatePublicationInitiale
        // console.log('targetPhotos changed in PostEditoPhotosUpload:', newPhotos ? newPhotos.length : 0) // debug

        // Déclencher la vérification des boutons
        this.$store.commit('socialPostsCreationPostEditorial/checkDraftButtonState')
        this.$store.commit('socialPostsCreationPostEditorial/checkPublishButtonState')
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    handleAddClick (event) {
      this.dialogVisible = true
    },
    handleFileSelect (file, fileList) {
      // Vérifier le nombre de fichiers
      if (this.fileList.length >= this.maximumPhotosAuthorizedToUpload) {
        this.closeDialog()
        this.$message({ message: this.$t('error.limitExceeded', { limitImages: this.maximumPhotosAuthorizedToUpload }), type: 'error' })
        return
      }

      // Vérifier la taille (10 MB max)
      if (file.size > 10000000) {
        this.closeDialog()
        this.$message({
          message: this.$t('error.fileTooLarge', { maxSize: '10 MB' }),
          type: 'error'
        })
        return
      }

      // Types acceptés : PNG, JPEG, WebP, AVIF, HEIC
      const acceptedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/avif', 'image/gif', 'image/heic', 'image/heif']
      const fileIsHeic = isHeicFile(file)

      // Accepter aussi les HEIC même si le type MIME est vide (fallback sur extension)
      const isAcceptedType = acceptedTypes.includes(file.raw.type) || fileIsHeic

      if (isAcceptedType) {
        this.fileList.push(file)

        // Informer l'utilisateur pour les fichiers HEIC
        if (fileIsHeic) {
          this.$message({
            message: this.$t('success.heicWillBeConverted', { filename: file.name }),
            type: 'info',
            duration: 3000
          })
        }
      } else {
        this.closeDialog()
        this.$message({ 
          message: this.$t('error.formatImageNotAllowed') || 'Format d\'image non autorisé. Formats acceptés: JPG, PNG, WebP, HEIC', 
          type: 'error' 
        })
      }
    },
    limitUploadExceeded () {
      this.closeDialog()
      this.$message({ message: this.$t('error.limitExceeded', { limitImages: this.maximumPhotosAuthorizedToUpload }), type: 'error' })
    },
    closeDialog () {
      this.dialogVisible = false
    },
    async addImages () {
      const self = this

      // Vérifier si on a des fichiers HEIC à convertir
      const hasHeicFiles = this.fileList.some(file => isHeicFile(file))

      if (hasHeicFiles) {
        self.convertingHeic = true
      }

      try {
        // Convertir tous les fichiers en parallèle
        const promises = this.fileList.map(async (file) => {
          const fileIsHeic = isHeicFile(file)

          if (fileIsHeic) {
            try {
              // Convertir le HEIC en JPEG côté client
              const jpegDataUrl = await convertHeicToJpeg(file.raw, 0.9)

              const newPhoto = {
                type: 'base64',
                url: jpegDataUrl
              }

              // Créer un nouveau tableau pour déclencher le setter de la computed property
              const currentPhotos = self.targetPhotos || []
              const updatedPhotos = [...currentPhotos, newPhoto]
              self.targetPhotos = updatedPhotos

              self.$forceUpdate()
              // Déclencher la vérification des boutons après ajout de photos
              self.$store.commit('socialPostsCreationPostEditorial/checkDraftButtonState')
              self.$store.commit('socialPostsCreationPostEditorial/checkPublishButtonState')
            } catch (error) {
              console.error('Erreur conversion HEIC:', error)
              self.$message({
                message: self.$t('error.heicConversionFailed', { filename: file.name }),
                type: 'error',
                duration: 5000
              })
            }
          } else {
            // Pour les autres formats, créer une prévisualisation normale
            return new Promise((resolve) => {
              const previewReader = new FileReader()

              previewReader.onload = function (readerEvt) {
                const newPhoto = { type: 'base64', url: readerEvt.target.result }

                // Créer un nouveau tableau pour déclencher le setter de la computed property
                const currentPhotos = self.targetPhotos || []
                const updatedPhotos = [...currentPhotos, newPhoto]
                self.targetPhotos = updatedPhotos

                self.$forceUpdate()
                // Déclencher la vérification des boutons après ajout de photos
                self.$store.commit('socialPostsCreationPostEditorial/checkDraftButtonState')
                self.$store.commit('socialPostsCreationPostEditorial/checkPublishButtonState')
                // La mise à jour des statuts médias est gérée par updateMediaType() dans PostEditoCreatePublicationInitiale
                resolve()
              }

              previewReader.readAsDataURL(file.raw)
            })
          }
        })

        // Attendre que toutes les conversions soient terminées
        await Promise.all(promises)
      } finally {
        self.convertingHeic = false
        this.dialogVisible = false
        this.fileList = []
      }
    },
    handleRemove (photo) {
      const index = this.targetPhotos.findIndex(v => v.url === photo.url)
      if (index > -1) {
        const updatedPhotos = [...this.targetPhotos]
        updatedPhotos.splice(index, 1)
        this.targetPhotos = updatedPhotos

        // Mettre à jour aussi les photos du currentPost
        const currentPost = this.$store.getters['postManager/getCurrentPost']
        if (currentPost && currentPost.photos) {
          // Trouver et supprimer la photo correspondante dans currentPost.photos
          const photoToRemove = currentPost.photos.find(p => p.url === photo.url)
          if (photoToRemove) {
            const updatedCurrentPostPhotos = currentPost.photos.filter(p => p.url !== photo.url)
            // Mettre à jour le currentPost avec les nouvelles photos
            this.$store.commit('postManager/updateCurrentPostData', {
              ...currentPost,
              photos: updatedCurrentPostPhotos
            })
          }
        }

        // Déclencher la vérification des boutons après suppression de photos
        this.$store.commit('socialPostsCreationPostEditorial/checkDraftButtonState')
        this.$store.commit('socialPostsCreationPostEditorial/checkPublishButtonState')

        // Mettre à jour tous les statuts médias après suppression
        this.$nextTick(() => {
          this.updateAllPhotosStatus()
        })
      }
    },
    onChange () {
      this.$forceUpdate()
    },
    getNestedValue (obj, path) {
      return path.split('.').reduce((current, part) => current && current[part], obj)
    },
    setNestedValue (obj, path, value) {
      const parts = path.split('.')
      const last = parts.pop()
      const target = parts.reduce((current, part) => {
        if (!current[part]) {
          this.$set(current, part, {})
        }
        return current[part]
      }, obj)
      this.$set(target, last, value)
    },
    async updateAllPhotosStatus () {
      // La mise à jour des statuts médias est gérée par updateMediaType() dans PostEditoCreatePublicationInitiale
      // Plus besoin d'appeler analyzePostMedias ici
      // console.log('updateAllPhotosStatus called - delegating to updateMediaType')
    }
  },
  computed: {
    isFieldDisabled () {
      return this.desactiverChamp || this.internalDisabled || this.maximumPhotosAuthorizedToUpload <= 0
    },
    dragOptions () {
      return {
        animation: 0,
        group: 'description',
        disabled: this.isFieldDisabled,
        ghostClass: 'ghost'
      }
    },
    maximumPhotosAuthorizedToUpload () {
      if (this.targetPhotos) {
        const remaining = 10 - this.targetPhotos.length
        this.internalDisabled = remaining <= 0
        return remaining
      }
      return 10
    },
    targetPhotos: {
      get () {
        const path = `${this.network}.pictures`
        return this.getNestedValue(this.post, path) || []
      },
      set (value) {
        const path = `${this.network}.pictures`
        this.setNestedValue(this.post, path, value)
        this.$store.commit('socialPostsCreationPostEditorial/setPhotosForNetwork', {
          network: this.network,
          photos: value
        })
      }
    }
  },
  beforeMount () {
    if (!this.targetPhotos) {
      this.targetPhotos = []
    }
  }
}
</script>
